import React, { ReactElement, useEffect } from "react";
import { showRequestErrorToast } from "../services/ErrorService";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { Loading } from "./loading/Loading";
import { useDefects } from "../queries/notableItemQueries";
import { sendGAEvent } from "../services/AnalyticsService";
import { GA_BUTTON_EVENT } from "../constants/GaEvents";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { DefectItems } from "@idwal/idwal-react-components";
import { AppContext } from "../App";
import { AppContextType } from "../types/AppContextType";
import { useAppDispatch, useAppSelector } from "../redux/hooks";
import {
    defectCodeChipsSelector,
    getRegimeMappingsThunk,
    regimeMappingsStatusSelector
} from "../features/inspectionsSlice";
import { listedDefects } from "../utils/regimeMappings";
import { useDefectCodeChips } from "../hooks/DefectCodes";

interface DefectsProps {
    regimeCodesOnly: boolean;
}

export const Defects = ({ regimeCodesOnly }: DefectsProps): ReactElement => {
    const { t } = useTranslation("locale");
    const { inspectionId } = useParams();
    const { user } = useAuthenticator((context) => [
        context.authStatus,
        context.user
    ]);
    const { earlyRelease } = React.useContext(AppContext) as AppContextType;
    // A Pre-PSC filters on these whether or not the chips are released, as
    // its PDF does.
    const mappings = useAppSelector(defectCodeChipsSelector);
    const mappingsStatus = useAppSelector(regimeMappingsStatusSelector);
    const { defectCodes, descriptions } = useDefectCodeChips();
    const dispatch = useAppDispatch();

    // The mappings are fetched once per user, so a failed fetch would leave a
    // Pre-PSC on the error until a reload. Opening the tab tries again.
    useEffect(() => {
        if (regimeCodesOnly && mappingsStatus === "failed") {
            dispatch(getRegimeMappingsThunk());
        }
    }, []);

    if (!inspectionId) {
        console.error("No inspection id.");
        showRequestErrorToast("Defects");
        return <div />;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO(A2-9305): guard clause above returns before this hook runs
    const { data, isError, error, isLoading } = useDefects(inspectionId, user);

    if (isLoading || data == undefined) return <Loading />;

    const defects = listedDefects(data, {
        regimeCodesOnly,
        mappingsStatus,
        chips: mappings
    });

    if (defects === "loading") return <Loading />;

    if (isError || defects === "failed") {
        console.error(isError ? error : "Regime mappings unavailable.");
        showRequestErrorToast("Defects");
        return <div />;
    }

    if (defects.length === 0) {
        return (
            <div
                className="flex justify-content-center align-items-center h-3rem"
                style={{ backgroundColor: "#FCFCFC" }}
            >
                {t("defects.noDefectItems")}
            </div>
        );
    }

    const handleAnalyticsEvent = (action: string) => {
        sendGAEvent(GA_BUTTON_EVENT, action);
    };

    if (earlyRelease) {
        return (
            <DefectItems
                notableItems={[]}
                sendAnalyticsEvent={handleAnalyticsEvent}
                showImageGrid={false}
            />
        );
    }

    return (
        <DefectItems
            notableItems={defects}
            sendAnalyticsEvent={handleAnalyticsEvent}
            showImageGrid={false}
            defectCodes={defectCodes}
            descriptions={descriptions}
            showIdwalCode={!regimeCodesOnly}
        />
    );
};
